/**
 * @fileoverview Password-reset completion page. Reads the tokenized link's
 * `token` query param, lets the user choose a new password, and on success
 * signs them in (the backend starts a fresh session). Shared by the instructor
 * and student portals via the `portal` prop.
 *
 * An organization's invitation link opens it too (`invite=1`). That page first
 * asks what the invitation is for, without using the link up, and names it
 * above the form: the organization and, for a program viewer, the programs
 * shared with them. A link that no longer works (used, expired, or for
 * someone who already has a password) says so up front and offers Sign in,
 * rather than a form that would only fail. If it can't ask, the page reads
 * as before.
 */

import { Fragment, useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, KeyRound, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { getErrorMessage } from "@/lib/api";

const PORTALS = {
  instructor: {
    subtitle: "Instructor Portal",
    endpoint: "/auth/reset-password",
    successPath: "/dashboard",
    loginPath: "/login",
    forgotPath: "/forgot-password",
  },
  student: {
    subtitle: "Student Portal",
    endpoint: "/student/auth/reset-password",
    successPath: "/student/dashboard",
    loginPath: "/student/login",
    forgotPath: "/student/forgot-password",
  },
  organization: {
    // Administrators and program viewers both sign in here.
    subtitle: "Organization portal",
    endpoint: "/org/auth/reset-password",
    successPath: "/org/instructors",
    loginPath: "/org/login",
    forgotPath: "/org/forgot-password",
    // What an invitation link is for, read without using it.
    invitationEndpoint: "/org/auth/invitation",
  },
  parent: {
    subtitle: "Parent Portal",
    endpoint: "/parent/auth/reset-password",
    successPath: "/parent",
    loginPath: "/parent/login",
    forgotPath: "/parent/forgot-password",
  },
  platform: {
    subtitle: "Hans Ops",
    endpoint: "/ops/auth/reset-password",
    successPath: "/ops",
    loginPath: "/ops/login",
    forgotPath: "/ops/forgot-password",
  },
};

/** "A", "A and B", "A, B and C" — each title set apart, since a title can hold a comma. */
function listedTitles(titles) {
  return titles.map((title, i) => (
    <Fragment key={`${i}-${title}`}>
      {i === 0 ? "" : i === titles.length - 1 ? " and " : ", "}
      <span className="font-medium text-[var(--text-primary)]">{title}</span>
    </Fragment>
  ));
}

const primaryButtonCls =
  "w-full bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium rounded-full py-3 hover:scale-105 active:scale-95 transition-transform text-sm disabled:opacity-50";

/**
 * In place of the form when an invitation's link no longer works. Used and
 * expired look the same from here (a used link is gone), so it offers both
 * ways on: sign in, or a new link.
 */
function InvitationGone({ loginPath, forgotPath }) {
  return (
    <div className="space-y-4" data-testid="invitation-gone">
      <div className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-2xl p-6 space-y-2 text-center">
        <p className="text-[var(--text-primary)] text-sm">It has already been used, or it has expired.</p>
        <p className="text-[var(--text-secondary)] text-sm">If you&rsquo;ve already chosen your password, sign in.</p>
      </div>
      <Button asChild className={primaryButtonCls}>
        <Link to={loginPath} data-testid="invitation-sign-in">
          <LogIn className="w-4 h-4 mr-2" />
          Sign in
        </Link>
      </Button>
      <p className="text-[var(--text-tertiary)] text-xs text-center leading-relaxed">
        Haven&rsquo;t chosen one yet?{" "}
        <Link to={forgotPath} className="text-[var(--accent-yellow-text)] hover:underline" data-testid="invitation-new-link">
          Get a new link
        </Link>
        , or ask whoever invited you to send another.
      </p>
    </div>
  );
}

/** Who invited them to what, and the address they will sign in with. */
function InvitationLine({ invitation }) {
  const programs = invitation.programs || [];
  return (
    <div data-testid="invitation-context">
      <p className="text-[var(--text-secondary)] text-sm leading-relaxed" data-testid="invitation-what">
        {programs.length ? (
          <>
            {invitation.org_name} has shared {listedTitles(programs)} with you on Hans.
          </>
        ) : (
          <>You&rsquo;ve been added to {invitation.org_name} on Hans.</>
        )}
      </p>
      {invitation.email && (
        <p className="text-[var(--text-tertiary)] text-xs mt-2" data-testid="invitation-email">
          After this, you&rsquo;ll sign in under Organization with {invitation.email}.
        </p>
      )}
    </div>
  );
}

const ResetPasswordPage = ({ portal = "instructor" }) => {
  const config = PORTALS[portal] || PORTALS.instructor;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  // An invitation's link (services/password_reset.send_invite_email): the
  // reader is choosing their first password, not resetting one.
  const invited = searchParams.get("invite") === "1";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  // What the invitation is for: null while asking, "gone" when the link no
  // longer works, false when it couldn't ask.
  const asking = Boolean(invited && token && config.invitationEndpoint);
  const [invitation, setInvitation] = useState(null);
  const gone = invitation === "gone";

  useEffect(() => {
    if (!asking) return undefined;
    let live = true;
    (async () => {
      try {
        const res = await axios.post(`${API}${config.invitationEndpoint}`, { token }, { withCredentials: true });
        if (live) setInvitation(res?.data?.org_name ? res.data : false);
      } catch (err) {
        // 404: used, expired, or not one the reset would accept. Anything
        // else (offline, a server error) proves nothing about the link, so
        // the form stays and says so itself if it fails.
        if (live) setInvitation(err?.response?.status === 404 ? "gone" : false);
      }
    })();
    return () => {
      live = false;
    };
  }, [asking, config.invitationEndpoint, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!password) {
      nextErrors.password = "Password is required";
    } else if (password.length < 8) {
      nextErrors.password = "Password must be at least 8 characters";
    }
    if (password !== confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match";
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await axios.post(`${API}${config.endpoint}`, { token, password }, { withCredentials: true });
      toast.success("Password updated. You're signed in.");
      navigate(config.successPath);
    } catch (err) {
      toast.error(getErrorMessage(err, "This reset link is invalid or has expired."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 studio-spotlight">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
            <span className="text-lg font-semibold text-[var(--text-primary)] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Hans
            </span>
          </div>
          <h1 className="text-2xl font-light text-[var(--text-primary)] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            {gone ? "This invitation link no longer works" : invited ? "Choose your password" : "Choose a new password"}
          </h1>
          {gone ? (
            <p className="text-[var(--text-secondary)] text-sm">{config.subtitle}</p>
          ) : invitation ? (
            <InvitationLine invitation={invitation} />
          ) : asking && invitation === null ? (
            // Held open while asking, so the page doesn't jump when it answers.
            <p className="text-sm min-h-[20px]" aria-hidden="true" data-testid="invitation-pending" />
          ) : (
            <p className="text-[var(--text-secondary)] text-sm">{config.subtitle}</p>
          )}
        </div>

        {gone ? (
          <InvitationGone loginPath={config.loginPath} forgotPath={config.forgotPath} />
        ) : !token ? (
          <div className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-2xl p-6 text-center space-y-3" data-testid="missing-token">
            <p className="text-[var(--text-primary)] text-sm">This reset link is missing or invalid.</p>
            <Link to={config.forgotPath} className="text-[var(--accent-yellow-text)] text-sm hover:underline inline-block">
              Request a new link
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-2xl p-6 space-y-4">
              <div>
                <Label className="text-[var(--text-tertiary)] text-xs uppercase tracking-wider">New Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors((p) => ({ ...p, password: undefined })); }}
                  required
                  minLength={8}
                  aria-invalid={errors.password ? "true" : undefined}
                  className={`mt-1 bg-[var(--studio-base)] text-[var(--text-primary)] ${errors.password ? "border-[var(--state-danger)]" : "border-[var(--studio-divider-strong)]"}`}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                />
                {errors.password ? (
                  <p className="text-[var(--state-danger)] text-xs mt-1" data-testid="password-error">{errors.password}</p>
                ) : (
                  <p className="text-[var(--text-tertiary)] text-xs mt-1" data-testid="password-hint">
                    Use at least 8 characters, with a mix of letters, numbers, and symbols.
                  </p>
                )}
              </div>
              <div>
                <Label className="text-[var(--text-tertiary)] text-xs uppercase tracking-wider">Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); if (errors.confirmPassword) setErrors((p) => ({ ...p, confirmPassword: undefined })); }}
                  required
                  minLength={8}
                  aria-invalid={errors.confirmPassword ? "true" : undefined}
                  className={`mt-1 bg-[var(--studio-base)] text-[var(--text-primary)] ${errors.confirmPassword ? "border-[var(--state-danger)]" : "border-[var(--studio-divider-strong)]"}`}
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                />
                {errors.confirmPassword && (
                  <p className="text-[var(--state-danger)] text-xs mt-1" data-testid="confirm-password-error">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <Button type="submit" disabled={submitting} className={primaryButtonCls}>
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <KeyRound className="w-4 h-4 mr-2" />
              )}
              {invited ? "Set password" : "Update password"}
            </Button>
          </form>
        )}

        {!gone && (
          <div className="text-center mt-6">
            <Link to={config.loginPath} className="text-[var(--text-tertiary)] text-xs hover:text-[var(--text-primary)] transition-colors block">
              Back to sign in
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
